import { Modal } from "@9amhealth/shared";
import styled from "@emotion/styled";
import { useMediaQuery } from "@react-hooks-library/core";
import type { FC } from "react";
import React from "react";
import { ArrowLeftIcon, IconCrossXCloseMissed } from "src/constants/icons";
import { APP_BREAKPOINT } from "src/constants/layout";
import StatusBarBloc from "src/hybrid/components/StatusBarBloc";
import { useBloc } from "src/state/state";

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  flex: 0 0 auto;
  background: var(--color-cream);
  min-height: 3rem;

  @media (min-width: ${APP_BREAKPOINT}px) {
    padding: 0.75rem 0.4rem;
  }
`;

const HeaderTitle = styled.div`
  font-size: 1rem;
  font-weight: 500;
  text-align: center;
  flex: 1 1 auto;
`;

const HeaderSlot = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  min-width: 2.5rem;
  justify-content: center;
`;

const IconButton = styled.button`
  background: transparent;
  border: 0;
  padding: 0.25rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 1.5rem;
    height: auto;
    color: var(--color-charcoal);
    display: block;
  }
`;

const Content = styled.div<{
  $hideHeaderOnMobile: boolean;
  $isMobile: boolean;
}>`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  background: var(--color-cream);
  padding: ${({ $isMobile, $hideHeaderOnMobile }) =>
    $isMobile && $hideHeaderOnMobile ? "0" : $isMobile ? "0 1.6rem" : "1rem"};
  padding-bottom: calc(var(--safe-area-inset-bottom, 0px) + 12rem);

  @media (min-width: ${APP_BREAKPOINT}px) {
    padding-bottom: calc(var(--safe-area-inset-bottom, 0px) + 6rem);
  }
`;

interface ProfileDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onBack?: () => void;
  title?: string;
  children?: React.ReactNode;
  hideHeaderOnMobile?: boolean;
}

const ProfileDialog: FC<ProfileDialogProps> = ({
  isOpen,
  onClose,
  onBack,
  title,
  children,
  hideHeaderOnMobile
}) => {
  const isMobile = useMediaQuery(`(max-width: ${APP_BREAKPOINT}px)`);
  const [{ title: statusBarTitle }] = useBloc(StatusBarBloc);

  const showHeader = !(isMobile && hideHeaderOnMobile);
  const headerTitle = title ?? (onBack ? statusBarTitle : undefined);

  return (
    <Modal isOpen={isOpen} variant="center" onDidDismiss={onClose}>
      {showHeader && (
        <Header>
          <HeaderSlot>
            {onBack && (
              <IconButton
                type="button"
                aria-label="Back"
                onClick={onBack}
                className="btn-icon"
              >
                <ArrowLeftIcon aria-hidden="true" />
              </IconButton>
            )}
          </HeaderSlot>

          {headerTitle ? (
            <HeaderTitle>{headerTitle}</HeaderTitle>
          ) : (
            <HeaderTitle />
          )}

          <HeaderSlot>
            {!onBack && (
              <IconButton
                type="button"
                aria-label="Close"
                onClick={onClose}
                className="btn-icon"
              >
                <IconCrossXCloseMissed aria-hidden="true" />
              </IconButton>
            )}
          </HeaderSlot>
        </Header>
      )}

      <Content
        $isMobile={isMobile}
        $hideHeaderOnMobile={Boolean(hideHeaderOnMobile)}
      >
        {children}
      </Content>
    </Modal>
  );
};

export default ProfileDialog;
